import { addCalendarDays, calendarDayInZone, instantFromWallClock, wallClockInZone } from '../zonedTime';
import type { WorkOrderVisitStatus } from './visits';
import type { BoardWaits } from './dispatchBoard';
import { missingSubSkills, type SubSkillLike } from './labour';

/**
 * 🔒 The Schedule view (piece 29; decisions 1911, 2081; T1049): one row per
 * field technician against the hours of a day, the technician's month beside it
 * (decision 6021), each booked visit a block, and the jobs still to book beside the grid. These are the
 * rules and the shapes that need no database. 🔨 No map, no live position and
 * no service territory: the view reads the calendar and the people as they are.
 */

/** The hours the day's grid draws, from the first to the last, in the Organization's zone. */
export const SCHEDULE_FIRST_HOUR = 6;
export const SCHEDULE_LAST_HOUR = 20;

/** 💰 A drop books this many hours: no job carries an estimate of its own, so the window is the board's default visit (decision 2081). */
export const SCHEDULE_DROP_HOURS = 4;

/** 🔒 The week a day sits in, Monday first, as seven calendar days. */
export function weekOf(day: string): string[] {
  const [year, month, date] = day.split('-').map(Number) as [number, number, number];
  const weekday = new Date(Date.UTC(year, month - 1, date)).getUTCDay();
  const monday = addCalendarDays(day, -((weekday + 6) % 7));
  return Array.from({ length: 7 }, (_, index) => addCalendarDays(monday, index));
}

/** 🔒 The instants the week's seven days span in the zone: Monday's midnight to the next Monday's. */
export function weekRange(day: string, zone: string): { from: Date; to: Date } {
  const week = weekOf(day);
  return { from: instantFromWallClock(`${week[0]}T00:00`, zone), to: instantFromWallClock(`${addCalendarDays(week[6]!, 1)}T00:00`, zone) };
}

/** 🔒 The month a day sits in, as its calendar days, the first to the last (decision 6021). */
export function monthOf(day: string): string[] {
  const month = day.slice(0, 7);
  const days: string[] = [];
  for (let cursor = `${month}-01`; cursor.slice(0, 7) === month; cursor = addCalendarDays(cursor, 1)) days.push(cursor);
  return days;
}

/** 🔒 The instants a day's month spans in the zone: the first's midnight to the next month's first (decision 6021). */
export function monthRange(day: string, zone: string): { from: Date; to: Date } {
  const days = monthOf(day);
  return { from: instantFromWallClock(`${days[0]}T00:00`, zone), to: instantFromWallClock(`${addCalendarDays(days[days.length - 1]!, 1)}T00:00`, zone) };
}

/**
 * 🔒 The month a technician's row draws (decision 6021): Monday-first weeks from
 * the week holding the first to the week holding the last, a day outside the
 * month drawn as null, so a short month draws four or five rows and never six.
 */
export function monthWeeksOf(day: string): (string | null)[][] {
  const days = monthOf(day);
  const month = day.slice(0, 7);
  const weeks: (string | null)[][] = [];
  for (let monday = weekOf(days[0]!)[0]!; monday <= days[days.length - 1]!; monday = addCalendarDays(monday, 7)) {
    weeks.push(weekOf(monday).map((d) => (d.slice(0, 7) === month ? d : null)));
  }
  return weeks;
}

/** 🔒 A drop's window in the zone: the wall clock it starts at, and the drop's length after it, as the booking path reads them. */
export function dropWindow(startsAt: string, zone: string): { start: Date; endsAt: string } {
  const start = instantFromWallClock(startsAt, zone);
  return { start, endsAt: wallClockInZone(new Date(start.getTime() + SCHEDULE_DROP_HOURS * 3_600_000), zone) };
}

/** The calendar day a visit's window starts on, in the zone. */
export function visitDayOf(windowStart: string, zone: string): string {
  return calendarDayInZone(windowStart, zone);
}

/**
 * 🔒 Where a block sits on the day's grid: its start and end as hours from
 * midnight in the zone, clipped to the drawn hours, or null where the visit
 * falls wholly outside them on that day. A visit with no end is drawn the
 * drop's length. The hours drawn are the grid's unless a narrower track names
 * its own, as the Dispatch Board's strip does at phone width (decision 3070).
 */
export function scheduleBlockPlacement(
  windowStart: string,
  windowEnd: string | null,
  day: string,
  zone: string,
  hours: { firstHour: number; lastHour: number } = { firstHour: SCHEDULE_FIRST_HOUR, lastHour: SCHEDULE_LAST_HOUR },
): { from: number; to: number } | null {
  const hourOf = (instant: string): number => {
    const wall = wallClockInZone(instant, zone);
    const onDay = wall.slice(0, 10);
    const [hours, minutes] = wall.slice(11, 16).split(':').map(Number) as [number, number];
    const at = hours + minutes / 60;
    if (onDay < day) return 0;
    if (onDay > day) return 24;
    return at;
  };
  const end = windowEnd ?? new Date(new Date(windowStart).getTime() + SCHEDULE_DROP_HOURS * 3_600_000).toISOString();
  const from = Math.max(hourOf(windowStart), hours.firstHour);
  const to = Math.min(hourOf(end), hours.lastHour);
  return to > from ? { from, to } : null;
}

/**
 * 🔒 Only a Scheduled or Dispatched visit moves (decision 2250): a visit that is
 * started, finished or cancelled stays where it happened.
 */
export function moveVisitProblem(status: WorkOrderVisitStatus): string | null {
  if (status === 'Scheduled' || status === 'Dispatched') return null;
  return `a visit that is ${status === 'InProgress' ? 'In Progress' : status} does not move`;
}

/** Whether a block drags on the grid: the same rule the move route holds. */
export function scheduleBlockMoves(status: WorkOrderVisitStatus): boolean {
  return moveVisitProblem(status) === null;
}

/**
 * 🔒 T1573 (decision 2350): a visit's window stays inside one calendar day in the zone, so a window
 * that runs past that day's midnight is refused; ending at midnight sharp is the day's own close.
 */
export function windowCrossesMidnight(start: Date, end: Date, zone: string): boolean {
  return calendarDayInZone(new Date(Math.max(end.getTime() - 1, start.getTime())), zone) !== calendarDayInZone(start, zone);
}

/** Why a drop is refused: the end would not follow the start, or the window would run past midnight. */
export type DragRefusal = 'endsBeforeStart' | 'crossesMidnight';

interface DragCandidate {
  startsAt: string;
  endsAt: string;
  start: Date;
  end: Date;
}

function dragCandidate(
  block: { windowStart: string; windowEnd: string | null },
  drop: { day: string; hour: number; edge: 'start' | 'end' },
  zone: string,
): DragCandidate {
  const start = new Date(block.windowStart).getTime();
  const end = block.windowEnd ? new Date(block.windowEnd).getTime() : start + SCHEDULE_DROP_HOURS * 3_600_000;
  const at = (hour: number): string => `${drop.day}T${String(hour).padStart(2, '0')}:00`;
  if (drop.edge === 'start') {
    // The visit keeps its own length wherever its start lands.
    const from = instantFromWallClock(at(drop.hour), zone);
    const to = new Date(from.getTime() + (end - start));
    return { startsAt: at(drop.hour), endsAt: wallClockInZone(to, zone), start: from, end: to };
  }
  const startsAt = wallClockInZone(new Date(start), zone);
  const closes = drop.hour + 1;
  const endsAt = closes >= 24 ? `${addCalendarDays(drop.day, 1)}T00:00` : at(closes);
  return { startsAt, endsAt, start: new Date(start), end: instantFromWallClock(endsAt, zone) };
}

/** The reason a drop is refused, or null where its window stands (decision 2350). */
export function dragRefusal(
  block: { windowStart: string; windowEnd: string | null },
  drop: { day: string; hour: number; edge: 'start' | 'end' },
  zone: string,
): DragRefusal | null {
  const window = dragCandidate(block, drop, zone);
  if (window.end.getTime() <= window.start.getTime()) return 'endsBeforeStart';
  return windowCrossesMidnight(window.start, window.end, zone) ? 'crossesMidnight' : null;
}

/**
 * 🔒 A dragged block's new window as wall clocks in the zone (decision 2250):
 * moved to a row's hour, it keeps its own length; its trailing edge dropped on an
 * hour ends it at that hour's close and keeps its start. A visit with no end
 * reads the drop's length. Null where `dragRefusal` names a reason: the end would
 * not follow the start, or the window would run past midnight (decision 2350).
 */
export function dragWindow(
  block: { windowStart: string; windowEnd: string | null },
  drop: { day: string; hour: number; edge: 'start' | 'end' },
  zone: string,
): { startsAt: string; endsAt: string } | null {
  if (dragRefusal(block, drop, zone) !== null) return null;
  const { startsAt, endsAt } = dragCandidate(block, drop, zone);
  return { startsAt, endsAt };
}

/**
 * 🔒 What a technician is short of on a job (T1248; decisions 0464, 2081): the
 * highest level its lines ask for where theirs is lower or unset, and each
 * sub-skill its lines need that they hold neither alone nor under a skill held
 * whole. Each reads as a warning chip, never a refusal.
 */
export function scheduleShortOf(
  held: { level: number | null; subSkillIds: readonly string[]; skillIds: readonly string[] },
  need: { levels: readonly { level: number; name: string }[]; subSkills: readonly SubSkillLike[] },
): string[] {
  const top = [...need.levels].sort((a, b) => b.level - a.level)[0];
  const level = top && (held.level === null || held.level < top.level) ? [`Needs ${top.name}`] : [];
  const unique = [...new Map(need.subSkills.map((row) => [row.id, row])).values()];
  return [...level, ...missingSubSkills(unique, held.subSkillIds, held.skillIds).map((row) => `Needs ${row.name}`)];
}

/**
 * 🔒 Decision 2282: the chip for each certification a job's checklists ask for
 * that the technician holds no Current completion of, worded as a sub-skill
 * short is. 🔨 A warning and never a refusal (the owner's answer of 2026-10-07).
 */
export function trainingWarnings(need: readonly { id: string; name: string }[], held: ReadonlySet<string> | undefined): string[] {
  return need.filter((row) => !held?.has(row.id)).map((row) => trainingNeedText(row.name));
}

/** 🔒 Decision 2282: one certification short as the chip reads it, *Needs* and its name. */
export function trainingNeedText(name: string): string {
  return `Needs ${name}`;
}

/** 🔒 The two filters (T1248): one home shop or every one, and a level and above or every level; a technician with no level passes only the second's every. */
export function technicianMatches(row: { homeShopSiteId: string | null; skillLevel: number | null }, filter: { homeShopSiteId: string | null; minLevel: number | null }): boolean {
  if (filter.homeShopSiteId !== null && row.homeShopSiteId !== filter.homeShopSiteId) return false;
  if (filter.minLevel !== null && (row.skillLevel === null || row.skillLevel < filter.minLevel)) return false;
  return true;
}

/** One booked visit on a technician's row. */
export interface ScheduleBlock {
  visitId: string;
  workOrderId: string;
  workOrderNumber: string;
  visitLabel: string;
  companyName: string;
  siteName: string;
  windowStart: string;
  windowEnd: string | null;
  status: WorkOrderVisitStatus;
  isLead: boolean;
  /** 🔒 T1248: what this technician is short of on the job, each a warning chip; empty where they meet it. */
  warnings: string[];
  /** The visit's version as the view read it, which the move sends back so a visit changed since is refused (piece 7; T1573). */
  version: number;
  /** 🔒 Decision 3100: why the visit cannot move, which draws it Fixed and asks before a drag moves it; null where it Can move. */
  fixedReason: string | null;
}

/** One field technician's row: who they are, their visits on the day, and their visit count on each day of the week. */
export interface ScheduleTechnician {
  employmentId: string;
  personId: string;
  name: string;
  title: string | null;
  skillLevelName: string | null;
  skillLevel: number | null;
  /** The employment's dispatch origin, the home shop; null where none is set. */
  homeShopSiteId: string | null;
  homeShopName: string | null;
  /** The sub-skills held, by name, and the skills held whole. */
  subSkills: string[];
  blocks: ScheduleBlock[];
  /** Visits on each of the week's seven days, Monday first. */
  week: number[];
  /** 🔒 Decision 6021: the days of the viewed day's month holding a visit for the technician, in the zone; absent from an older reply, which marks none. */
  monthBooked?: string[];
  /** 🔒 Decision 5572: whether the technician holds an active login; absent from an older reply, which reads as held. */
  hasLogin?: boolean;
}

/**
 * One job still to book or to crew (decision 5570): a Draft or Scheduled job with no visit standing, or whose visit
 * still to come names nobody. The Dispatch Board lists the same jobs under the same name.
 */
export interface ScheduleUnscheduledJob {
  workOrderId: string;
  /** 🔒 Decision 5570: what the job waits on, a visit or a crew on its visit, which the row says; absent from an older reply. */
  waits?: BoardWaits;
  /** 🔒 Decision 5570: the visit still to come with nobody on it, which a drop or Book Here crews at the hour chosen; null where no visit stands. */
  visitId?: string | null;
  workOrderNumber: string;
  status: string;
  companyName: string;
  siteName: string;
  priorityTierName: string;
  isEmergency: boolean;
  /** The highest level a line asks for, and the sub-skills its lines need, by name. */
  requiredLevelName: string | null;
  requiredSubSkills: string[];
}

export interface ScheduleViewAnswer {
  day: string;
  zone: string;
  week: string[];
  firstHour: number;
  lastHour: number;
  technicians: ScheduleTechnician[];
  unscheduled: ScheduleUnscheduledJob[];
  /** The home shops the rows carry, for the filter. */
  homeShops: { siteId: string; name: string }[];
  /** The skill levels the rows carry, lowest first, for the filter. */
  levels: { level: number; name: string }[];
  canBook: boolean;
}

/** What a drop or a Book here sends: the job, the technician, and the wall clock it starts at in the zone. */
export interface ScheduleBookInput {
  workOrderId: string;
  employmentId: string;
  startsAt: string;
  timeZone: string;
  /** 🔒 Decision 4340: the wall clock it ends at, the job's own length on the Dispatch Board; absent books the drop's four hours. */
  endsAt?: string | null;
}

/**
 * What a dragged block sends (decision 2250): the worker it was dragged from,
 * the worker it lands on, and its new start and end as wall clocks in the zone.
 */
export interface ScheduleMoveInput {
  fromEmploymentId: string;
  toEmploymentId: string;
  startsAt: string;
  endsAt: string;
  timeZone: string;
  /** The visit's version the view read; a visit that has changed since is refused (piece 7; T1573). */
  version?: number | null;
}

/** What a move answers: any warning a chip names for the worker it landed on. */
export interface ScheduleMoveAnswer {
  visitId: string;
  warnings: string[];
  /** 🔒 Decision 12850: the visit falls outside the worker's working pattern, which the notice says; the booking stands. Absent reads as inside. */
  outsidePattern?: boolean;
}

/** What a booking answers: the visit it wrote, and any warning a chip names. */
export interface ScheduleBookAnswer {
  visitId: string;
  warnings: string[];
  /** 🔒 Decision 12850: the visit falls outside the worker's working pattern, which the notice says; the booking stands. Absent reads as inside. */
  outsidePattern?: boolean;
}
